import { Box, Button, ButtonBase, Card, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { Star, StarBorder } from '@mui/icons-material'
import UnfoldLessRounded from '@mui/icons-material/UnfoldLessRounded'
import UnfoldMoreRounded from '@mui/icons-material/UnfoldMoreRounded'
import AddRounded from '@mui/icons-material/AddRounded'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiFile, apiGet, apiPut } from '../../generic/Api_Functions'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import { SmartDocumentViewer } from './SmartDocumentViewer'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { useToggle } from '../../generic/hooks/useToggle'
import DocumentThumbnail from './DocumentThumbnail'
import { isAllowedMainImage } from '../parts/MainImageThumbnail'
import AlertUI from '../../generic/AlertUI'
import { getDefaultDocumentName, partitionUploadFiles } from './documentBatchUpload'
import { reorderDocuments } from './documentReorder'
import { buildDocumentCreateUrl, buildDocumentUpdateUrl } from './documentApiUrls'

const buildGetAllUrl = (resourceType, referenceId, referenceTempUUID) => {
  if (referenceTempUUID) {
    return referenceId
      ? 'document/getAll/' + resourceType + '/' + referenceId + '/' + referenceTempUUID
      : 'documents/getAll/' + resourceType + '/' + referenceTempUUID
  }
  return 'document/getAll/' + resourceType + '/' + referenceId
}

// ⚠️ Historia 148 — TRES PROPS ADITIVAS, todas con el valor por defecto que deja el comportamiento
// de hoy intacto (MIC oficina, el editor de workflows y el widget de instrucciones no pasan
// ninguna):
//
//   · `documents` — cuando el consumidor YA TIENE los documentos, se pintan esos y este componente
//     NO consulta. Lo necesita el modal de documentacion de MOS: alli los documentos llegan del
//     endpoint agregador, YA FILTRADOS por los permisos del perfil en el servidor. Si el carrusel
//     se los buscase por su cuenta con `document/getAll/...` mostraria TODOS los del registro,
//     incluidos los que Sun habia filtrado, y ademas por un camino sin contexto MOS que el gate
//     no mira. Seria una fuga de documentacion, no un detalle de implementacion.
//   · `contextQuery` — los 4 query params del contexto MOS, que viajan al visor Y a las
//     miniaturas (las miniaturas tambien descargan el fichero).
//   · `onLoadError` — deja que el consumidor distinga «ya no existe» (404) de un fallo de red.
//   · `onDocumentChange` — avisa de QUE documento se esta mostrando. El indice vive dentro de
//     este componente (flechas, miniaturas), asi que sin este aviso el consumidor no puede
//     mantener sincronizada su propia seleccion: el operario ve un documento y tiene otro
//     marcado en su lista.
const DocumentsCarouselView = ({ referenceId, referenceUUID, referenceTempUUID, forceReloadDocuments = false, resourceType, renderCustomHeader = null, initialDocumentUUID = null, showDocumentsHeader = true, canManageMainImage = false, mainImageDocumentId = '', onToggleMainImage = null, thumbnailsCollapsed = false, onToggleThumbnails = null, enableUpload = false, enableReorder = false, documents: providedDocuments = '', contextQuery = '', onLoadError, onDocumentChange }) => {
  const [fetchedDocuments, setFetchedDocuments] = useState([])
  // Cadena vacia = el consumidor no los aporta, asi que se buscan (comportamiento de siempre).
  const documentsAreProvided = Array.isArray(providedDocuments)
  const documents = documentsAreProvided ? providedDocuments : fetchedDocuments
  // Con los documentos ya aportados, el indice arranca YA en el documento pedido: si arrancara en 0
  // el componente anunciaria el primero del registro antes de centrarse, y quien escuche ese aviso
  // se lo creeria. El efecto de mas abajo sigue cubriendo el caso en que los documentos llegan
  // despues (los que se busca el propio componente).
  const [documentIndex, setDocumentIndex] = useState(() => {
    if (!Array.isArray(providedDocuments) || !initialDocumentUUID) {
      return 0
    }
    const initialIndex = providedDocuments.findIndex((item) => item.uuid === initialDocumentUUID)
    return initialIndex >= 0 ? initialIndex : 0
  })
  const { t_ } = useSmartTranslation()
  const [requestFullScreen, onRequestFullscreen] = useToggle()
  const [AlertElement, showAlert] = AlertUI()
  const [documentTypes, setDocumentTypes] = useState([])
  const [maxFileSize, setMaxFileSize] = useState(undefined)
  const uploadInputRef = useRef(null)
  const draggedIndexRef = useRef(null)

  const loadDocuments = useCallback(async () => {
    // Con los documentos aportados no se consulta: ese es justo el punto de la prop.
    if (documentsAreProvided) {
      return
    }
    if (!resourceType || (!referenceTempUUID && !referenceId)) {
      setFetchedDocuments([])
      return
    }
    const data = await apiGet(buildGetAllUrl(resourceType, referenceId, referenceTempUUID))
    setFetchedDocuments(data)
  }, [referenceId, resourceType, referenceTempUUID, documentsAreProvided])

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments, forceReloadDocuments])

  // S9/S10/S23/S24 (HU-03/HU-07): the default document type and the size limit are only needed
  // when this view can create documents; other consumers (read-only viewers, monitoring cards)
  // never pay for these extra requests.
  useEffect(() => {
    if (!enableUpload) {
      return
    }
    const fetchUploadConfig = async () => {
      const types = await apiGet('documentTypes')
      setDocumentTypes(types)
      const maxSize = await apiGet('settings/MAX_FILE_SIZE')
      const size = maxSize?.value
      setMaxFileSize(size === null || size === undefined ? 1024 * 1024 * 1024 : parseInt(size) * 1024 * 1024)
    }
    fetchUploadConfig()
  }, [enableUpload])

  const defaultDocumentType = useMemo(
    () => documentTypes.find((documentType) => documentType?.name?.toLowerCase() === 'general') || {},
    [documentTypes]
  )

  // D7: valid files of the batch are uploaded, the non-admitted ones (no extension, oversized or
  // name too long) are rejected with a warning — never the whole batch (S10/S24).
  const handleUploadFiles = useCallback(async (files) => {
    const { accepted, rejected } = partitionUploadFiles(files, maxFileSize)
    if (rejected.length > 0) {
      showAlert({
        severity: 'warning',
        message: t_('The following files could not be uploaded: {{files}}', {
          files: rejected.map((entry) => entry.file.name).join(', ')
        })
      })
    }
    if (accepted.length === 0 || !defaultDocumentType.id) {
      return
    }
    const createUrl = buildDocumentCreateUrl({ resourceType, referenceId, referenceTempUUID, referenceUUID })
    // S9/S23: one DOCUMENT per file, named by default after the file name without its extension.
    // S11 (D8, sun): no order is read/sent by the client — Sun computes the append order
    // atomically inside PUT /document/create, so these calls are safe to fire concurrently.
    await Promise.all(accepted.map((file) => apiFile(createUrl, {
      documentName: getDefaultDocumentName(file.name),
      description: '',
      documentType: defaultDocumentType,
      order: 0
    }, file)))
    await loadDocuments()
  }, [maxFileSize, defaultDocumentType, resourceType, referenceId, referenceTempUUID, referenceUUID, showAlert, t_, loadDocuments])

  const handleDrop = (event) => {
    event.preventDefault()
    handleUploadFiles(Array.from(event.dataTransfer?.files || []))
  }

  const handleFileInputChange = (event) => {
    handleUploadFiles(Array.from(event.target.files || []))
    event.target.value = ''
  }

  // S21 (RN06.1/RN06.2 — HU-06): dragging a thumbnail to another position renumbers
  // "documentOrder" contiguously and persists it with the existing PUT /document/update.
  const handleReorderDrop = useCallback(async (destinationIndex) => {
    const sourceIndex = draggedIndexRef.current
    draggedIndexRef.current = null
    const reordered = reorderDocuments(documents, sourceIndex, destinationIndex)
    if (reordered === documents) {
      return
    }
    setFetchedDocuments(reordered)
    const previousOrderByUuid = new Map(documents.map((document) => [document.uuid, document.documentOrder]))
    const changedDocuments = reordered.filter((document) => previousOrderByUuid.get(document.uuid) !== document.documentOrder)
    const updateUrl = buildDocumentUpdateUrl({ referenceTempUUID, referenceUUID })
    await Promise.all(changedDocuments.map((document) => apiPut(updateUrl, document)))
    await loadDocuments()
  }, [documents, referenceTempUUID, referenceUUID, loadDocuments])

  useEffect(() => {
    if (!documents || !initialDocumentUUID) {
      return
    }
    const newIndex = documents.findIndex((item) => item.uuid === initialDocumentUUID)
    if (newIndex >= 0) {
      setDocumentIndex(newIndex)
    }
  }, [documents, initialDocumentUUID])

  const selectedDocumentUUID = documents?.[documentIndex]?.uuid

  useEffect(() => {
    if (!onDocumentChange || !selectedDocumentUUID) {
      return
    }
    onDocumentChange(documents[documentIndex])
    // Solo cuando cambia el documento MOSTRADO: la identidad del callback no debe redisparar esto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocumentUUID])

  // Make sure index is inside range
  const changeDocumentIndex = (increment) => {
    setDocumentIndex(prevIndex => {
      let newIndex = prevIndex + increment
      if (newIndex >= documents.length) {
        newIndex = 0
      } else if (newIndex < 0) {
        newIndex = documents.length - 1
      }
      return newIndex
    })
  }

  const selectedDocument = useMemo(() => {
    if (!documents) {
      return null
    }
    if (documents.length > 0 && documentIndex >= documents.length) {
      setDocumentIndex(0)
      return null
    }
    return documents[documentIndex]
  }, [documents, documentIndex])

  return (
    <Card
      sx={{ height: '100%', width: '100%', minWidth: 0 }}
      variant='outlined'
      data-testid={enableUpload ? 'documents-carousel-dropzone' : undefined}
      onDragOver={enableUpload ? (event) => event.preventDefault() : undefined}
      onDrop={enableUpload ? handleDrop : undefined}
    >
      {AlertElement}
      {enableUpload && (
        <input
          ref={uploadInputRef}
          type='file'
          multiple
          data-testid='documents-upload-input'
          aria-label={t_('Add documents')}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      )}
      <Stack alignItems='center' gap={1} sx={{ height: '100%' }}>
        {showDocumentsHeader && documents.length > 0 && !(onToggleThumbnails && thumbnailsCollapsed)
          ? (
            <Box
              sx={{
                width: '100%',
                padding: '0.25rem',
                minHeight: '7rem',
                overflowX: 'auto',
                scrollbarGutter: 'stable'
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'nowrap',
                  gap: 1,
                  height: '100%',
                  width: 'max-content'
                }}
              >
                {documents.map((document, index) => {
                  // Only image formats that can actually be marked as main image (isAllowedMainImage:
                  // jpg/jpeg/png/gif/bmp; webp/svg excluded) get the star.
                  const canMark = canManageMainImage && onToggleMainImage && isAllowedMainImage(document.extension)
                  const isMain = mainImageDocumentId !== '' && document.id === mainImageDocumentId
                  const markLabel = isMain ? t_('Remove main image') : t_('Set as main image')
                  // Star sits to the left of the thumbnail title. It lives inside the thumbnail's
                  // ButtonBase, so it renders as `ButtonBase component='span'` (not an IconButton) to
                  // avoid nesting a <button> in a <button> while still getting its OWN ripple. The
                  // mousedown stopPropagation prevents the thumbnail's ripple from also firing, so the
                  // click feedback stays on the star; the click stopPropagation keeps choosing the
                  // main image from changing the previewed document.
                  const markStar = canMark
                    ? (
                      <Tooltip title={markLabel}>
                        <ButtonBase
                          component='span'
                          aria-label={markLabel}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => { event.stopPropagation(); onToggleMainImage(document) }}
                          sx={{
                            borderRadius: '50%',
                            padding: '2px',
                            color: isMain ? 'warning.main' : 'action.active',
                            '&:hover': { color: isMain ? 'warning.dark' : 'text.primary' }
                          }}
                        >
                          {isMain ? <Star fontSize='small' /> : <StarBorder fontSize='small' />}
                        </ButtonBase>
                      </Tooltip>
                      )
                    : null
                  return (
                    <div
                      key={document.uuid || index}
                      draggable={enableReorder}
                      onDragStart={enableReorder ? () => { draggedIndexRef.current = index } : undefined}
                      onDragOver={enableReorder ? (event) => event.preventDefault() : undefined}
                      onDrop={enableReorder ? (event) => { event.preventDefault(); event.stopPropagation(); handleReorderDrop(index) } : undefined}
                    >
                      <DocumentThumbnail
                        document={document}
                        tempUUID={referenceTempUUID}
                        contextQuery={contextQuery}
                        isSelected={index === documentIndex}
                        onSelect={() => setDocumentIndex(index)}
                        titleAdornment={markStar}
                      />
                    </div>
                  )
                })}
              </Box>
            </Box>
            )
          : null}
        <Card sx={{ width: '100%', padding: '0.5rem', minHeight: 48, display: 'flex', alignItems: 'center' }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ width: '100%', height: '100%' }}>
            <Stack direction='row' alignItems='center' gap={0.5} sx={{ flex: 1, minWidth: 0 }}>
              {onToggleThumbnails && documents.length > 0 && (
                <Tooltip title={t_('Thumbnails')}>
                  <IconButton size='small' aria-label={t_('Thumbnails')} onClick={() => onToggleThumbnails()} sx={{ flexShrink: 0 }}>
                    {thumbnailsCollapsed ? <UnfoldMoreRounded /> : <UnfoldLessRounded />}
                  </IconButton>
                </Tooltip>
              )}
              {selectedDocument
                ? (
                  <Box sx={{ flex: 1, minWidth: 60, overflow: 'hidden' }}>
                    <Tooltip title={selectedDocument.documentName + ' - ' + selectedDocument.fileName}>
                      <Typography variant='h6' noWrap sx={{ textOverflow: 'ellipsis' }}>
                        {selectedDocument.documentName + ' - ' + selectedDocument.fileName}
                      </Typography>
                    </Tooltip>
                  </Box>
                  )
                : <div />}
            </Stack>
            <Stack direction='row' alignItems='center' gap={1} sx={{ flexShrink: 0 }}>
              {documents.length > 1
                ? (
                  <Stack direction='row' alignItems='center' gap={1}>
                    <Button onClick={() => changeDocumentIndex(-1)}><NavigateBeforeIcon /></Button>
                    <Box>{(documentIndex + 1) + '/' + documents.length}</Box>
                    <Button onClick={() => changeDocumentIndex(1)}><NavigateNextIcon /></Button>
                  </Stack>
                  )
                : null}
              {enableUpload && (
                <Tooltip title={t_('Add documents')}>
                  <IconButton aria-label={t_('Add documents')} onClick={() => uploadInputRef.current?.click()}>
                    <AddRounded />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton onClick={() => onRequestFullscreen()}><FullscreenIcon /></IconButton>
              {renderCustomHeader}
            </Stack>
          </Stack>
        </Card>
        <Box sx={{ width: '100%', height: '100%', flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {selectedDocument
            ? <SmartDocumentViewer
                extension={selectedDocument.extension}
                documentUUID={selectedDocument.uuid}
                documentName={selectedDocument.documentName}
                sensitive={selectedDocument.sensitive === true}
                tempUUID={selectedDocument.id ? undefined : referenceTempUUID}
                requestFullScreen={requestFullScreen}
                contextQuery={contextQuery}
                onLoadError={onLoadError}
              />
            : <Typography variant='h5'>{t_('No documents found')}</Typography>}
        </Box>
      </Stack>
    </Card>
  )
}

export default DocumentsCarouselView
